import { supabase } from './supabase';

export type ProposalStatus = 'draft' | 'pending' | 'approved' | 'declined' | 'expired';
export type ProposalEventType = 'sent' | 'opened' | 'downloaded' | 'approved' | 'declined' | 'reminder';

export interface Proposal {
  id: string;
  workspace_id: string;
  created_by: string;
  client_user_id: string | null;
  client_email: string;
  client_name: string;
  company_name: string;
  title: string;
  cover_image_url: string;
  content: any[];
  status: ProposalStatus;
  valid_until: string | null;
  sent_at: string | null;
  opened_at: string | null;
  signed_at: string | null;
  signature_data: string;
  signer_name: string;
  decline_reason: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

export interface ProposalTemplate {
  id: string;
  workspace_id: string;
  created_by: string;
  name: string;
  cover_image_url: string;
  content: any[];
  created_at: string;
  updated_at: string;
}

export interface ProposalEvent {
  id: string;
  proposal_id: string;
  event_type: ProposalEventType;
  actor_user_id: string | null;
  metadata: Record<string, any>;
  created_at: string;
}

export interface CreateProposalData {
  workspace_id: string;
  title?: string;
  client_user_id?: string | null;
  client_email?: string;
  client_name?: string;
  company_name?: string;
  cover_image_url?: string;
  content?: any[];
  valid_until?: string | null;
}

export interface TitleSettings {
  fontSize: number;
  letterSpacing: number;
  textCase: 'uppercase' | 'lowercase' | 'capitalize' | 'none';
  color: string;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  alignment?: 'left' | 'center' | 'right';
  fontFamily?: string;
  lineHeight?: number | string;
}

export interface CoverData {
  image: string;
  color: string;
  bgType?: 'solid' | 'gradient';
  gradient?: string;
  gradientSettings?: {
    type: 'linear' | 'radial';
    angle: number;
    stops: { color: string; position: number }[];
  };
  footerBgColor?: string;
  footerBgType?: 'solid' | 'gradient';
  footerGradient?: string;
  footerGradientSettings?: {
    type: 'linear' | 'radial';
    angle: number;
    stops: { color: string; position: number }[];
  };
  footerOpacity?: number;
  footerSettings?: {
    leftTitle: string;
    rightTitle: string;
    leftContent: string;
    rightContent: string;
  };
  footerStyleSettings?: {
    titleColor: string;
    titleSize: number;
    textColor: string;
    textSize: number;
  };
  titleSettings: TitleSettings;
}

export function parseCoverData(data: string | null | undefined): CoverData {
  const defaultColor = '#E8E4DC';
  const defaultTitleSettings: TitleSettings = {
    fontSize: 72,
    letterSpacing: -0.03,
    textCase: 'capitalize',
    color: '#111827',
    isBold: true,
    isItalic: false,
    isUnderline: false,
    alignment: 'left',
    fontFamily: 'Inter',
    lineHeight: 1.05,
  };

  const defaultFooterSettings = {
    leftTitle: 'CREATED BY:',
    rightTitle: 'PREPARED FOR:',
    leftContent: '[Sender.FirstName] [Sender.LastName]\n[Sender.Company]',
    rightContent: '[Client.FirstName] [Client.LastName]\n[Client.Company]',
  };

  const defaultFooterStyleSettings = {
    titleColor: '#111111',
    titleSize: 20,
    textColor: '#333333',
    textSize: 14,
  };

  const defaultFooterGradientSettings = {
    type: 'linear' as const,
    angle: 135,
    stops: [
      { color: '#667eea', position: 0 },
      { color: '#764ba2', position: 100 }
    ]
  };

  const emptyBase = { image: '', color: defaultColor, bgType: 'solid' as const, gradient: '', gradientSettings: undefined, footerBgColor: '#ffffff', footerBgType: 'solid' as const, footerGradient: '', footerGradientSettings: defaultFooterGradientSettings, footerOpacity: 1, footerSettings: defaultFooterSettings, footerStyleSettings: defaultFooterStyleSettings, titleSettings: defaultTitleSettings };

  if (!data) return emptyBase;
  if (data.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(data);
      return {
        image: parsed.image || '',
        color: parsed.color || defaultColor,
        bgType: parsed.bgType || 'solid',
        gradient: parsed.gradient || '',
        gradientSettings: parsed.gradientSettings || {
          type: 'linear',
          angle: 135,
          stops: [
            { color: '#FF9A9E', position: 0 },
            { color: '#FECFEF', position: 100 }
          ]
        },
        footerBgColor: parsed.footerBgColor || '#ffffff',
        footerBgType: parsed.footerBgType || 'solid',
        footerGradient: parsed.footerGradient || '',
        footerGradientSettings: parsed.footerGradientSettings || defaultFooterGradientSettings,
        footerOpacity: parsed.footerOpacity !== undefined ? parsed.footerOpacity : 1,
        footerSettings: parsed.footerSettings || defaultFooterSettings,
        footerStyleSettings: parsed.footerStyleSettings || defaultFooterStyleSettings,
        titleSettings: { ...defaultTitleSettings, ...(parsed.titleSettings || {}) },
      };
    } catch {
      return { ...emptyBase, image: data };
    }
  }
  return { ...emptyBase, image: data };
}

export const proposalService = {
  async listProposals(workspaceId: string): Promise<Proposal[]> {
    try {
      await supabase.rpc('mark_expired_proposals', { p_workspace_id: workspaceId });
    } catch {
      // Ignore if RPC execution is restricted or unauthenticated
    }
    const { data, error } = await supabase
      .from('proposals')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async listClientProposals(userId: string): Promise<Proposal[]> {
    const { data, error } = await supabase
      .from('proposals')
      .select('*')
      .eq('client_user_id', userId)
      .in('status', ['pending', 'approved', 'declined', 'expired'])
      .order('sent_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async getProposal(id: string): Promise<Proposal | null> {
    const { data, error } = await supabase
      .from('proposals')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async getProposalBySlug(slug: string): Promise<Proposal | null> {
    const { data, error } = await supabase
      .from('proposals')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
    if (error) throw error;
    return data;
  },

  async createProposal(payload: CreateProposalData): Promise<Proposal> {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('proposals')
      .insert({
        workspace_id: payload.workspace_id,
        created_by: userData.user.id,
        title: payload.title || 'Untitled Proposal',
        client_user_id: payload.client_user_id || null,
        client_email: payload.client_email || '',
        client_name: payload.client_name || '',
        company_name: payload.company_name || '',
        cover_image_url: payload.cover_image_url || '',
        content: payload.content || [],
        valid_until: payload.valid_until || null,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateProposal(id: string, updates: Partial<Proposal>): Promise<Proposal> {
    const { data, error } = await supabase
      .from('proposals')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async sendProposal(id: string): Promise<Proposal> {
    const proposal = await this.updateProposal(id, {
      status: 'pending',
      sent_at: new Date().toISOString(),
    });
    await this.recordEvent(id, 'sent', {});
    return proposal;
  },

  async recordOpen(id: string): Promise<void> {
    const { data: proposal } = await supabase
      .from('proposals')
      .select('opened_at, client_user_id')
      .eq('id', id)
      .maybeSingle();
    if (!proposal) return;
    if (!proposal.opened_at) {
      await supabase
        .from('proposals')
        .update({ opened_at: new Date().toISOString() })
        .eq('id', id);
    }
    await this.recordEvent(id, 'opened', {});
  },

  async approveProposal(id: string, signerName: string, signatureData: string): Promise<Proposal> {
    const now = new Date().toISOString();
    const proposal = await this.updateProposal(id, {
      status: 'approved',
      signed_at: now,
      signer_name: signerName,
      signature_data: signatureData,
    });
    await this.recordEvent(id, 'approved', { signer_name: signerName });
    return proposal;
  },

  async declineProposal(id: string, reason: string): Promise<Proposal> {
    const proposal = await this.updateProposal(id, {
      status: 'declined',
      decline_reason: reason,
    });
    await this.recordEvent(id, 'declined', { reason });
    return proposal;
  },

  async deleteProposal(id: string): Promise<void> {
    const { error } = await supabase.from('proposals').delete().eq('id', id);
    if (error) throw error;
  },

  async listTemplates(workspaceId: string): Promise<ProposalTemplate[]> {
    const { data, error } = await supabase
      .from('proposal_templates')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  async saveAsTemplate(proposalId: string, name: string): Promise<ProposalTemplate> {
    const proposal = await this.getProposal(proposalId);
    if (!proposal) throw new Error('Proposal not found');
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('proposal_templates')
      .insert({
        workspace_id: proposal.workspace_id,
        created_by: userData.user.id,
        name,
        cover_image_url: proposal.cover_image_url,
        content: proposal.content,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase.from('proposal_templates').delete().eq('id', id);
    if (error) throw error;
  },

  async createFromTemplate(templateId: string, workspaceId: string): Promise<Proposal> {
    const { data: template, error } = await supabase
      .from('proposal_templates')
      .select('*')
      .eq('id', templateId)
      .maybeSingle();
    if (error) throw error;
    if (!template) throw new Error('Template not found');
    return this.createProposal({
      workspace_id: workspaceId,
      title: template.name,
      cover_image_url: template.cover_image_url,
      content: template.content,
    });
  },

  async recordEvent(
    proposalId: string,
    type: ProposalEventType,
    metadata: Record<string, any> = {}
  ): Promise<void> {
    const { data: userData } = await supabase.auth.getUser();
    await supabase.from('proposal_events').insert({
      proposal_id: proposalId,
      event_type: type,
      actor_user_id: userData.user?.id || null,
      metadata,
    });
  },

  async getEvents(proposalId: string): Promise<ProposalEvent[]> {
    const { data, error } = await supabase
      .from('proposal_events')
      .select('*')
      .eq('proposal_id', proposalId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  },
};
